import { api } from '@/lib/api/client'
import type { Priority, Visibility, WorkItemType } from '@/types/domain'
import type { WorkItem } from '@/types/work'
import { fetchAll } from '@/lib/api/paged'

export interface WorkItemFilters {
  project?: string
  assignee?: 'me'
  open?: boolean
  q?: string
  /** A saved view's id or slug. The server applies its filter to the same query. */
  view?: string
}

export async function fetchWorkItems(filters: WorkItemFilters = {}): Promise<WorkItem[]> {
  return fetchAll<WorkItem>('/work-items', filters as Record<string, string | number | boolean>)
}

/**
 * One item, in full. The list endpoints return a lighter shape — no parent, no
 * children — because a board with three hundred cards should not carry a tree
 * it never draws.
 */
export async function fetchWorkItem(id: string): Promise<WorkItem> {
  const { data } = await api.get<{ data: WorkItem }>(`/work-items/${id}`)

  return data.data
}

export async function moveWorkItem(
  id: string,
  move: { stateId: string; afterId?: string | undefined; beforeId?: string | undefined },
): Promise<WorkItem> {
  const { data } = await api.post<{ data: WorkItem }>(`/work-items/${id}/move`, move)

  return data.data
}

export async function createWorkItem(input: {
  projectId: string
  title: string
  stateId?: string
  /** Set to open the new item as a child of another. */
  parentId?: string
}): Promise<WorkItem> {
  const { data } = await api.post<{ data: WorkItem }>('/work-items', input)

  return data.data
}

/**
 * PATCH means "change what I sent".
 *
 * Absent and null are different instructions on the server, so every field here
 * is optional and an explicit null is how an assignee or a date gets cleared —
 * sending `{ assigneeId: null }` unassigns, sending nothing leaves it alone.
 */
export interface WorkItemChanges {
  title?: string
  description?: string | null
  type?: WorkItemType
  visibility?: Visibility
  assigneeId?: string | null
  priority?: Priority
  /** A uuid or a human key — the server resolves either. */
  parentId?: string | null
  startDate?: string | null
  dueDate?: string | null
  estimate?: number | null
  stateId?: string | null
}

export async function updateWorkItem(id: string, changes: WorkItemChanges): Promise<WorkItem> {
  const { data } = await api.patch<{ data: WorkItem }>(`/work-items/${id}`, changes)

  return data.data
}

export interface Label {
  id: string
  name: string
  color: string | null
  usageCount?: number
}

export async function fetchLabels(): Promise<Label[]> {
  const { data } = await api.get<{ data: Label[] }>('/labels')

  return data.data
}

/** Labels are shared across the organization; the server refuses a duplicate name. */
export async function createLabel(name: string, color?: string): Promise<Label> {
  const { data } = await api.post<{ data: Label }>('/labels', color === undefined ? { name } : { name, color })

  return data.data
}

export async function attachLabel(workItemId: string, labelId: string): Promise<void> {
  await api.post(`/work-items/${workItemId}/labels`, { labelId })
}

export async function detachLabel(workItemId: string, labelId: string): Promise<void> {
  await api.delete(`/work-items/${workItemId}/labels/${labelId}`)
}

/** Watching is only ever about yourself — the server uses the caller's own id. */
export async function fetchWatchers(workItemId: string): Promise<{ id: string; name: string }[]> {
  const { data } = await api.get<{ data: { id: string; name: string }[] }>(
    `/work-items/${workItemId}/watchers`,
  )

  return data.data
}

export async function setWatching(workItemId: string, watching: boolean): Promise<void> {
  if (watching) {
    await api.post(`/work-items/${workItemId}/watchers`, {})
  } else {
    await api.delete(`/work-items/${workItemId}/watchers`)
  }
}

/**
 * `blocks` means this item blocks the other; `blockedBy` reverses it, so the
 * caller never has to construct the edge backwards.
 */
export async function linkWorkItems(
  workItemId: string,
  input: { workItemId: string; direction: 'blocks' | 'blockedBy' },
): Promise<void> {
  await api.post(`/work-items/${workItemId}/links`, input)
}

export async function unlinkWorkItems(workItemId: string, linkId: string): Promise<void> {
  await api.delete(`/work-items/${workItemId}/links/${linkId}`)
}

export async function deleteWorkItem(id: string): Promise<void> {
  await api.delete(`/work-items/${id}`)
}

export interface CodeLink {
  id: string
  kind: string
  reference: string
  title: string | null
  url: string | null
  state: string | null
  author: string | null
  mergedAt: string | null
  repository?: { id: string; provider: string; fullName: string } | null
}

/** Commits and pull requests the webhooks matched to this task by its key. */
export async function fetchCodeLinks(workItemId: string): Promise<CodeLink[]> {
  const { data } = await api.get<{ data: CodeLink[] }>(`/work-items/${workItemId}/code-links`)

  return data.data
}

/** Exactly what TriageController::present() returns — no more, no less. */
export interface TriageSuggestion {
  id: string
  workItemId: string
  type: string | null
  priority: string | null
  assigneeId: string | null
  confidence: number
  reason: string | null
  status: string
}

/** Ask the model what it thinks this is. It proposes; a person decides. */
export async function requestTriage(workItemId: string): Promise<TriageSuggestion> {
  const { data } = await api.post<{ data: TriageSuggestion }>(`/work-items/${workItemId}/triage`, {})

  return data.data
}

export async function respondToTriage(
  workItemId: string,
  suggestionId: string,
  status: 'accepted' | 'dismissed',
): Promise<TriageSuggestion> {
  const { data } = await api.patch<{ data: TriageSuggestion }>(
    `/work-items/${workItemId}/triage/${suggestionId}`,
    { status },
  )

  return data.data
}
